import { useCallback, useEffect, useRef, useState } from 'react';
import { invoiceCardId } from '../utils/invoiceReversal';
import type { InvoiceReversal } from './useInvoiceReversal';
import type { AgreementInvoice } from '../types/agreements.types';

export interface ReverseInvoiceDialog {
    target: AgreementInvoice | null;
    open: (invoice: AgreementInvoice) => void;
    close: () => void;
    submit: (note: string) => void;
}

const focusInvoiceCard = (invoiceId: string): void => {
    document.getElementById(invoiceCardId(invoiceId))?.querySelector<HTMLElement>('button[aria-expanded]')?.focus();
};

/** The action button leaves the card once the invoice is reversed, so focus returns to the card's toggle. */
export function useReverseInvoiceDialog(reversal: InvoiceReversal | undefined): ReverseInvoiceDialog {
    const [target, setTarget] = useState<AgreementInvoice | null>(null);
    const focusAfterClose = useRef<string | null>(null);
    const submitted = useRef(false);
    const { reverse, clearError } = reversal ?? {};

    useEffect(() => {
        if (target === null && focusAfterClose.current !== null) {
            focusInvoiceCard(focusAfterClose.current);
            focusAfterClose.current = null;
        }
    }, [target]);

    const open = useCallback(
        (invoice: AgreementInvoice) => {
            clearError?.();
            submitted.current = false;
            setTarget(invoice);
        },
        [clearError],
    );

    // A submit hides the opener while pending, so the Modal cannot restore focus to it.
    const close = useCallback(() => {
        clearError?.();
        if (submitted.current && target) focusAfterClose.current = target.id;
        setTarget(null);
    }, [clearError, target]);

    const submit = useCallback(
        (note: string) => {
            if (!target || !reverse) return;
            submitted.current = true;
            void reverse(target, note).then((reversed) => {
                if (!reversed) return;
                focusAfterClose.current = target.id;
                setTarget(null);
            });
        },
        [target, reverse],
    );

    return { target, open, close, submit };
}
